import { Box, IconButton, Button, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../../assets/errors/nodata.png'; // Adjust the path as necessary

const Newsletter = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [subscriberToDelete, setSubscriberToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    email: ''
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getData('proccess.php', 'newsletter_V2'); // Fetch newsletter data
        if (response.success) {
          setSubscribers(Array.isArray(response.data) ? response.data : []);
        } else {
          toast.error(response.message || "Failed to load newsletter data");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch subscribers");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  const handleAddClick = () => {
    setFormData({
      email: ''
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (subscriberItem) => {
    setSelectedSubscriber(subscriberItem);
    setFormData({
      email: subscriberItem.email || ''
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (subscriberItem) => {
    setSubscriberToDelete(subscriberItem);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData('proccess.php', 'newsletter_V2', subscriberToDelete.id); // Delete subscriber by ID
      if (response.success) {
        toast.success(`Subscriber deleted successfully`);
        setSubscribers(subscribers.filter(item => item.id !== subscriberToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete subscriber");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete subscriber");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setSubscriberToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      let response;
      const formDataToSend = { email: formData.email };

      if (selectedSubscriber) {
        // Update existing subscriber
        response = await updateData('proccess.php', 'newsletter_V2', formDataToSend, selectedSubscriber.id); // Update subscriber
        if (response.success) {
          toast.success(`Subscriber updated successfully`);
          setSubscribers(subscribers.map(item => 
            item.id === selectedSubscriber.id ? { ...item, ...formData } : item
          ));
        }
      } else {
        // Add new subscriber
        response = await addData('proccess.php', 'newsletter_V2', formDataToSend); // Add subscriber
        if (response.success) {
          toast.success(`Subscriber added successfully`);
          // Assuming response.data is either the new subscriber or the updated list
          if (Array.isArray(response.data)) {
            setSubscribers(response.data);
          } else {
            setSubscribers(prev => [...prev, response.data]);
          }
        }
      }

      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedSubscriber(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 20 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 150 },
    { field: "dateCreated", headerName: "Date Subscribed", flex: 1, minWidth: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          <IconButton
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.orange[400] }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No subscribers available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={openEditDialog || openAddDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedSubscriber(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedSubscriber ? "Edit Subscriber" : "Add New Subscriber"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{ width: "100%", padding: "1rem" }}>
            <Box display={"flex"} gap={"1rem"} width={"100%"} mb={"1rem"} flexWrap="wrap">
              <TextField
                style={{ width: "100%" }}
                id="email"
                name="email"
                label="Email Address"
                variant="standard"
                value={formData.email}
                onChange={handleFormChange}
                required
                type="email"
              />
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setOpenAddDialog(false);
              setSelectedSubscriber(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{ backgroundColor: colors.blue[400] }}
            variant="contained"
            endIcon={<SendIcon />}
          >
            {selectedSubscriber ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the subscriber "{subscriberToDelete?.email}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: colors.red[400] }}
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight={"bold"}
            sx={{ mb: "5px" }}
          >
            Newsletter Management
          </Typography>
          <Typography variant="h5" color={colors.blue[400]}>
            Manage newsletter subscribers
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{
            backgroundColor: colors.blue[400],
            '&:hover': {
              backgroundColor: colors.blue[500],
            }
          }}
        >
          Add Subscriber
        </Button>
      </Box>
      <Divider component="" />
      <Box sx={{ height: "45vh", width: "100%" }} backgroundColor={colors.dark[300]} padding={".2rem"} mt={"1rem"}>
        <DataGrid
          rows={subscribers}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          sx={{
            border: "none",
            ".MuiDataGrid-cell": {
              borderBottom: "none",
              backgroundColor: "none"
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: colors.primary[900],
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: colors.primary[900],
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Newsletter;